import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { defer } from '/imports/utils';
import { Icon, IconKey, IconProps } from '/imports/ui/Icon';
import { Logo } from '/imports/ui/Logo';
import { useUser } from '/imports/hooks';
import { strings } from '/imports/strings';
import { Account, Roles } from '/imports/api/accounts';
import { setSetting } from '/imports/api/users';
import { buildNavEntries } from '/imports/ui/navItems';

// Descendant utilities reproduce the old NavStyled nested rules. State classes
// (.collapsed / .open / .print-mode) toggled on the root are matched with
// [&.x]:; mobile media via the `mobile:` variant.
const navClass = [
  // No padding on the root: the brand band needs to reach both edges so its
  // bottom border spans the sidebar. The lists carry their own gutters instead.
  'bg-panel border-r border-soft flex flex-col max-w-[232px] min-w-[232px] transition-[max-width] duration-300 ease',
  // Collapsed keeps the MARK visible and drops only the wordmark, so the
  // sidebar still says whose app it is. (It used to zero the image instead.)
  '[&.collapsed]:max-w-14 [&.collapsed]:min-w-14',
  '[&_.app-pages-nav]:flex-1 [&_.app-pages-nav]:overflow-auto [&_.app-pages-nav]:mb-2 [&_.app-pages-nav]:px-2 [&_.app-pages-nav]:pt-2',
  '[&_.app-pages-nav_.nav-group-label]:text-[11px] [&_.app-pages-nav_.nav-group-label]:font-semibold [&_.app-pages-nav_.nav-group-label]:tracking-caps [&_.app-pages-nav_.nav-group-label]:uppercase [&_.app-pages-nav_.nav-group-label]:text-fg-3 [&_.app-pages-nav_.nav-group-label]:px-2.5 [&_.app-pages-nav_.nav-group-label]:pt-4 [&_.app-pages-nav_.nav-group-label]:pb-1',
  '[&_.app-pages-nav_.nav-group-label:first-child]:pt-[0.4rem]',
  '[&_.nav-item-accordion_.accordion-arrow]:ml-auto [&_.nav-item-accordion_.accordion-arrow]:mr-2 [&_.nav-item-accordion_.accordion-arrow]:transition-transform [&_.nav-item-accordion_.accordion-arrow]:duration-200 [&_.nav-item-accordion_.accordion-arrow]:ease [&_.nav-item-accordion_.accordion-arrow.expanded]:rotate-180',
  '[&_.nav-item-accordion_.nav-sub-items_.nav-sub-item]:h-8 [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item]:pl-9',
  '[&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.text]:text-[12.5px] [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.text]:font-normal [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.text]:text-fg-3 [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.text]:pr-3',
  '[&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:ml-auto [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:my-auto [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:min-w-[1.4rem] [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:h-[1.15rem] [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:px-[0.4rem] [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:rounded-md [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:text-xs [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:font-bold [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:text-fg-3 [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:bg-page-alt [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:border [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:border-soft [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:inline-flex [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:items-center [&_.nav-item-accordion_.nav-sub-items_.nav-sub-item_.sub-badge]:justify-center',
  '[&_.nav-item-accordion_.nav-sub-items_.nav-sub-item:hover_.text]:text-fg-1',
  '[&.print-mode]:hidden',
  'mobile:fixed mobile:z-[3] mobile:resize-none mobile:w-mobile-nav mobile:left-[-16rem] mobile:transition-[left] mobile:duration-200 mobile:ease mobile:overflow-visible mobile:h-full mobile:box-border',
  'mobile:[&.open]:left-0',
].join(' ');

export interface NavItemConfig {
  path: string,
  text?: string,
  icon?: IconKey,
  children?: React.ReactNode,
  sectionLabel?: string,
}

interface NavProps {
  account?: Account;
  items?: (NavItemConfig | null)[],
  excludeFooterItems?: ('orgs' | 'users' | 'orgUsers')[],
}

export const Nav = (props: NavProps) => {
  const account = props.account;
  const [mobileOpen, setMobileOpen] = useState(false);
  // Selection derives from the router, never from local state: the sidebar is
  // no longer the only navigation source (Command Palette, back/forward), and a
  // click-tracked copy of the path goes stale the moment anything else routes.
  const { pathname } = useLocation();
  const isSelected = (path: string) => pathname === path || pathname.startsWith(path + '/');
  const [collapsed, setCollapsed] = useState(false);
  const user = useUser();

  const isAdminOrSuper = !!user?.isSuperUser || !!account?.roles?.includes(Roles.admin);
  const isSuperUser = !!user?.isSuperUser;
  // Built by the shared builder so the command palette offers exactly what the
  // sidebar does; duplicating the list guaranteed the two would drift.
  const commonItems = buildNavEntries({ isSuperUser, isAdminOrSuper }) as ({ sectionLabel: string } | NavItemProps)[];
  // Rendered directly rather than via state + effect: the effect's [user] deps
  // froze account-derived visibility (an org admin's DevTools entry) until an
  // unrelated user-doc change, and the list is cheap to build every render.
  const _items: ({ sectionLabel: string } | NavItemProps | null)[] = [...((props.items || []) as any[]), ...commonItems];

  // Sidebar shape is per-user and persisted through the same setSetting the
  // theme toggle uses, so it survives a reload and follows the account rather
  // than the browser.
  const pinned: string[] = user?.settings?.navPinned ?? [];
  const collapsedGroups: string[] = user?.settings?.navCollapsed ?? [];
  const isPinned = (item: any) => !!item?.path && pinned.includes(item.path);

  const togglePin = (path: string) => {
    const next = pinned.includes(path) ? pinned.filter((p) => p !== path) : [...pinned, path];
    void setSetting.call({ key: 'navPinned', value: next });
  };
  const toggleGroup = (label: string) => {
    const next = collapsedGroups.includes(label)
      ? collapsedGroups.filter((g) => g !== label)
      : [...collapsedGroups, label];
    void setSetting.call({ key: 'navCollapsed', value: next });
  };

  // Pinned entries are LIFTED to the top rather than duplicated. Showing an item
  // in two places makes the list longer while making it harder to scan, which is
  // the opposite of what pinning is for.
  const pinnedItems = _items.filter((i) => i && !('sectionLabel' in i) && isPinned(i)) as NavItemProps[];
  const renderable: ({ sectionLabel: string } | NavItemProps | null)[] = [];
  let sectionOf = '';
  for (const item of _items) {
    if (!item) continue;
    if ('sectionLabel' in item) { sectionOf = item.sectionLabel; renderable.push(item); continue; }
    if (isPinned(item)) continue;
    renderable.push(item);
  }
  void sectionOf;

  const navItemFor = (item: NavItemProps & { children?: React.ReactNode }, keyPrefix = '') => (
    item.children ?
      <NavItem id={item.path} key={`${keyPrefix}${item.path}`} path={item.path} selected={isSelected(item.path)}>{item.children}</NavItem>
      :
      <NavItem
        id={item.path}
        key={`${keyPrefix}${item.path}`}
        icon={item.icon}
        text={item.text}
        path={item.path}
        selected={isSelected(item.path)}
        subItems={item.subItems}
        pinned={isPinned(item)}
        onTogglePin={() => togglePin(item.path)}
      />
  );

  return (
    <div id="sidenav" className={`nav ${navClass}${mobileOpen ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}
      onClick={() => { defer(() => mobileOpen && setMobileOpen(false)); }}
    >
      {/*
        Brand band. A bordered fixed-height row rather than a floating logo with
        a margin: it gives the sidebar a header the nav list can scroll under,
        and the subtitle says what the app IS, which the mark alone cannot.
        Collapsed, the mark centres and the wordmark is removed from the tree
        rather than hidden, so a screen reader does not announce a label the
        sidebar is not showing.
      */}
      <div
        className={[
          'logo-wrapper relative flex h-14 shrink-0 items-center gap-2.5 border-b border-soft px-3',
          '[&_.icon]:cursor-pointer [&_.icon]:transition-transform [&_.icon]:duration-300 [&_.icon]:ease',
          collapsed ? 'collapsed justify-center px-0' : '',
        ].join(' ')}
      >
        {/*
          Collapsed, the mark IS the expand control. A 56px rail cannot hold the
          mark and a separate chevron without both feeling cramped, and dropping
          the mark instead would leave the rail unbranded.
        */}
        <Logo
          size={1.75}
          className={collapsed ? 'shrink-0 cursor-pointer' : 'shrink-0'}
        />
        {collapsed && (
          <span
            role="button"
            aria-label="Expand sidebar"
            title="Expand sidebar"
            onClick={() => setCollapsed(false)}
            className="absolute inset-0 cursor-pointer"
          />
        )}
        {!collapsed && (
          <>
            <div className="min-w-0">
              <div className="truncate text-[15px] leading-none font-bold text-fg-1">MogBots</div>
              <div className="eyebrow mt-0.5 truncate text-fg-3">Operator Console</div>
            </div>
            <Icon
              className="ml-auto text-fg-3 hover:text-fg-1"
              onClick={() => setCollapsed(true)}
              icon="keyboardDoubleArrowLeft"
              size={18}
              tip="Collapse sidebar"
            />
          </>
        )}
      </div>
      <div className="app-pages-nav">
        {pinnedItems.length > 0 && (
          <>
            <div className="nav-group-label">{strings.Pinned()}</div>
            {pinnedItems.map((item) => navItemFor(item as any, 'pin-'))}
          </>
        )}
        {
          (() => {
            // Rendered with an explicit walk rather than a map so a collapsed
            // heading can swallow the items that follow it: the list is flat,
            // so "inside a group" is just "after its label and before the next".
            let hidden = false;
            return renderable.map((item, idx) => {
              if (!item) return null;
              if ('sectionLabel' in item) {
                const label = item.sectionLabel;
                const isCollapsed = collapsedGroups.includes(label);
                hidden = isCollapsed;
                return (
                  <button
                    key={`section-${idx}-${label}`}
                    type="button"
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleGroup(label)}
                    className="nav-group-label flex w-full cursor-pointer items-center gap-1 border-none bg-transparent text-left hover:text-fg-2"
                  >
                    <Icon icon="expandMore" size={14} className={isCollapsed ? '-rotate-90 transition-transform' : 'transition-transform'} />
                    {label}
                  </button>
                );
              }
              if (hidden) return null;
              return navItemFor(item as any);
            });
          })()
        }
      </div>
      <div
        className={[
          'mobile-overlay',
          'mobile:fixed mobile:top-0 mobile:right-0 mobile:h-full mobile:w-full mobile:bg-overlay mobile:opacity-0 mobile:pointer-events-none mobile:[transition:width_0.2s_ease,opacity_0.5s_ease]',
          mobileOpen ? 'open mobile:z-[3] mobile:opacity-30 mobile:pointer-events-auto mobile:w-[calc(100%-16rem)]' : '',
        ].join(' ')}
        onClick={() => setMobileOpen(false)}
      ></div>
      <div
        className={[
          'mobile-toggle hidden',
          'mobile:block mobile:absolute mobile:top-4 mobile:right-[-3rem]',
          '[&_.icon.hide]:hidden [&_svg]:w-8',
          mobileOpen ? 'open mobile:right-4 mobile:z-[2]' : '',
        ].join(' ')}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <Icon className={mobileOpen ? 'close' : ' hide close'} icon="close" />
        <Icon className={!mobileOpen ? 'menu' : ' hide menu'} icon="menu" />
      </div>
    </div>
  );
};

// Descendant utilities reproduce the old NavItemStyled (styled(Link)) rules.
const navItemClass = [
  'h-9 flex items-center rounded-md pr-2.5 relative no-underline text-fg-3 transition-colors duration-150',
  '[&_.icon]:my-auto [&_.icon]:ml-2.5',
  '[&_svg]:w-[17px]',
  'hover:bg-page-alt hover:text-fg-1',
  '[&.selected]:bg-primary/10 [&.selected]:text-primary',
  '[&.selected]:before:absolute [&.selected]:before:left-0 [&.selected]:before:top-1.5 [&.selected]:before:bottom-1.5 [&.selected]:before:w-0.5 [&.selected]:before:rounded-coin [&.selected]:before:bg-primary [&.selected]:before:content-[\'\']',
  '[&_.text]:text-[13px] [&_.text]:font-medium [&_.text]:text-current [&_.text]:flex [&_.text]:flex-col [&_.text]:justify-around [&_.text]:ml-2.5 [&_.text]:max-w-full [&_.text]:overflow-x-hidden [&_.text]:text-ellipsis [&_.text]:whitespace-nowrap [&_.text]:transition-colors [&_.text]:duration-200 [&_.text]:ease',
  '[&_.badge]:absolute [&_.badge]:top-1 [&_.badge]:left-1 [&_.badge]:bg-primary [&_.badge]:text-on-primary [&_.badge]:h-4 [&_.badge]:rounded-md [&_.badge]:px-[0.3rem] [&_.badge]:text-[10px] [&_.badge]:font-bold [&_.badge]:flex [&_.badge]:flex-col [&_.badge]:justify-around',
].join(' ');

interface NavSubItem {
  id: string;
  path: string;
  text: string;
  badge?: number;
  badgeColor?: string;
}

interface NavItemProps {
  id: string;
  icon?: IconKey;
  iconProps?: Partial<IconProps>;
  text?: string;
  textStyle?: React.CSSProperties;
  path: string;
  children?: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  subItems?: NavSubItem[];
  /** Shows the star filled and keeps it visible without hover. */
  pinned?: boolean;
  /** Omitted for items that cannot be pinned (the footer set). */
  onTogglePin?: () => void;
}

export const NavItem = (props: NavItemProps) => {
  const { id, icon, text, textStyle, path, children, selected, onClick, subItems, pinned, onTogglePin } = props;
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  if (subItems?.length) {
    return (
      <div className="nav-item-accordion">
        <Link
          id={id}
          className={`nav-item ${navItemClass}${selected ? ' selected' : ''}`}
          to={path}
          onClick={(e) => { e.preventDefault(); setExpanded(!expanded); onClick?.(); }}
        >
          {icon ? <Icon icon={icon} /> : null}
          {text && <span className="text" style={textStyle}>{text}</span> || null}
          <Icon className={`accordion-arrow${expanded ? ' expanded' : ''}`} icon="expandMore" />
        </Link>
        {expanded && (
          <div className="nav-sub-items">
            {subItems.map((sub) => (
              <Link key={sub.id} id={sub.id} className={`nav-item nav-sub-item ${navItemClass}${location.pathname === sub.path ? ' selected' : ''}`} to={sub.path} onClick={() => onClick?.()}>
                <span className="text">{sub.text}</span>
                {sub.badge ? <span className="sub-badge" style={sub.badgeColor ? { background: sub.badgeColor } : undefined}>{sub.badge}</span> : null}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <Link id={id} className={`nav-item group ${navItemClass}${selected ? ' selected' : ''}`} to={path} onClick={() => onClick?.()}>
      {icon ? <Icon icon={icon} /> : null}
      {
        children ?
          children
          :
          <>
            {text && <span className="text" style={textStyle}>{text}</span> || null}
          </>
      }
      {/*
        The star sits inside the Link, so it has to stop the click from
        navigating as well as from bubbling -- preventDefault alone would still
        run the row's onClick, and stopPropagation alone would still follow the
        href. It stays invisible until hover unless the item is pinned, so a
        long sidebar is not a column of grey stars.
      */}
      {onTogglePin && (
        <span
          role="button"
          aria-label={pinned ? 'Unpin' : 'Pin to top'}
          title={pinned ? 'Unpin' : 'Pin to top'}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(); }}
          className={`ml-auto shrink-0 cursor-pointer ${pinned ? 'text-gold' : 'text-fg-3 opacity-0 group-hover:opacity-100 hover:text-fg-1'}`}
        >
          {/* Solid when pinned, outline when merely hoverable: the fill IS the
              state, matching the reference's fill="currentColor" favourites. */}
          <Icon icon="star" size={13} fill={pinned ? 'currentColor' : undefined} />
        </span>
      )}
    </Link>
  );
};
